<template>
  <!--
    AiScanningOverlay.vue - Fullscreen overlay under AI-scanning
    =============================================================
    Viser en flot glassmorphism overlay når dokumenter analyseres.

    Features:
    - Fullscreen glassmorphism baggrund
    - CaseSummaryBar øverst
    - Progress for hver fil (starter forfra per dokument)
    - Sjove skiftende tekster
    - Smooth animationer
  -->
  <transition name="fade">
    <div
      v-if="isVisible"
      class="ai-scanning-overlay"
    >
      <div class="overlay-backdrop" />

      <div class="overlay-wrapper">
        <div class="overlay-content">
          <!-- CaseSummaryBar øverst i content -->
          <div class="overlay-summary-bar">
            <CaseSummaryBar />
          </div>

          <!-- Scanning content centreret -->
          <div class="scanning-center">
            <!-- Logo/Icon -->
            <div class="scanning-icon">
              <div class="icon-pulse">
                <feather-icon
                  icon="CpuIcon"
                  size="48"
                />
              </div>
            </div>

            <!-- Sjov tekst der skifter -->
            <h2 class="scanning-title">
              {{ currentFunText }}
            </h2>

            <!-- Status tekst -->
            <p class="scanning-status">
              {{ $t('DEBTIA_CREATE_AI_STATUS', { current: currentIndex, total: totalCount }) }}
            </p>

            <!-- Nuværende fil -->
            <div
              v-if="currentFileName"
              class="current-file"
            >
              <feather-icon
                icon="FileTextIcon"
                size="16"
                class="mr-50"
              />
              {{ currentFileName }}
            </div>

            <!-- Progress for NUVÆRENDE fil (0-100%, starter forfra per fil) -->
            <div class="progress-container">
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: `${currentFileProgress}%` }"
                />
              </div>
              <span class="progress-text">{{ currentFileProgress }}%</span>
            </div>

            <!-- Liste over alle filer og deres status -->
            <div
              v-if="files.length > 0"
              class="files-list"
            >
              <div
                v-for="(file, index) in files"
                :key="index"
                class="file-item"
                :class="{
                  'file-completed': file.status === 'completed',
                  'file-processing': file.status === 'processing',
                  'file-pending': file.status === 'pending',
                  'file-error': file.status === 'error'
                }"
              >
                <div class="file-info">
                  <feather-icon
                    v-if="file.status === 'completed'"
                    icon="CheckCircleIcon"
                    size="16"
                    class="file-icon text-success"
                  />
                  <div
                    v-else-if="file.status === 'processing'"
                    class="file-icon-spinner"
                  >
                    <feather-icon
                      icon="LoaderIcon"
                      size="16"
                      class="spinning"
                    />
                  </div>
                  <feather-icon
                    v-else-if="file.status === 'error'"
                    icon="XCircleIcon"
                    size="16"
                    class="file-icon text-danger"
                  />
                  <feather-icon
                    v-else
                    icon="CircleIcon"
                    size="16"
                    class="file-icon text-muted"
                  />
                  <span class="file-name">{{ truncateFileName(file.name) }}</span>
                </div>

                <!-- Mini progress bar for processing fil -->
                <div
                  v-if="file.status === 'processing'"
                  class="file-progress"
                >
                  <div
                    class="file-progress-fill"
                    :style="{ width: `${currentFileProgress}%` }"
                  />
                </div>
              </div>
            </div>

            <!-- Fun facts / tips mens man venter -->
            <div class="waiting-tips">
              <feather-icon
                icon="InfoIcon"
                size="14"
                class="mr-50"
              />
              <span>{{ currentTip }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api'
import i18n from '@/libs/i18n'
import CaseSummaryBar from './CaseSummaryBar.vue'

export default {
  name: 'AiScanningOverlay',

  components: {
    CaseSummaryBar,
  },

  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    currentFileProgress: {
      type: Number,
      default: 0,
    },
    currentFileName: {
      type: String,
      default: '',
    },
    files: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    // Fun texts that rotate (using i18n)
    const funTextKeys = [
      'DEBTIA_CREATE_AI_FUN_1',
      'DEBTIA_CREATE_AI_FUN_2',
      'DEBTIA_CREATE_AI_FUN_3',
      'DEBTIA_CREATE_AI_FUN_4',
      'DEBTIA_CREATE_AI_FUN_5',
      'DEBTIA_CREATE_AI_FUN_6',
      'DEBTIA_CREATE_AI_FUN_7',
      'DEBTIA_CREATE_AI_FUN_8',
      'DEBTIA_CREATE_AI_FUN_9',
      'DEBTIA_CREATE_AI_FUN_10',
      'DEBTIA_CREATE_AI_FUN_11',
      'DEBTIA_CREATE_AI_FUN_12',
    ]

    const funTexts = computed(() => funTextKeys.map(key => i18n.t(key)))

    // Tips while waiting (using i18n)
    const tipKeys = [
      'DEBTIA_CREATE_AI_TIP_1',
      'DEBTIA_CREATE_AI_TIP_2',
      'DEBTIA_CREATE_AI_TIP_3',
      'DEBTIA_CREATE_AI_TIP_4',
      'DEBTIA_CREATE_AI_TIP_5',
      'DEBTIA_CREATE_AI_TIP_6',
      'DEBTIA_CREATE_AI_TIP_7',
    ]

    const tips = computed(() => tipKeys.map(key => i18n.t(key)))

    const currentTextIndex = ref(0)
    const currentTipIndex = ref(0)
    let textInterval = null
    let tipInterval = null

    const currentFunText = computed(() => funTexts.value[currentTextIndex.value])
    const currentTip = computed(() => tips.value[currentTipIndex.value])

    // Skift tekst hver 2.5 sekund (hurtigere for at holde brugeren optaget)
    function startTextRotation() {
      textInterval = setInterval(() => {
        currentTextIndex.value = (currentTextIndex.value + 1) % funTexts.value.length
      }, 2500)

      tipInterval = setInterval(() => {
        currentTipIndex.value = (currentTipIndex.value + 1) % tips.value.length
      }, 5000)
    }

    function stopTextRotation() {
      if (textInterval) {
        clearInterval(textInterval)
        textInterval = null
      }
      if (tipInterval) {
        clearInterval(tipInterval)
        tipInterval = null
      }
    }

    // Truncate lange filnavne
    function truncateFileName(name) {
      if (!name) return ''
      if (name.length > 30) {
        return `${name.substring(0, 27)}...`
      }
      return name
    }

    // Start/stop rotation baseret på visibility
    watch(() => props.isVisible, newVal => {
      if (newVal) {
        startTextRotation()
        // Disable scroll på body
        document.body.style.overflow = 'hidden'
      } else {
        stopTextRotation()
        // Enable scroll igen
        document.body.style.overflow = ''
      }
    })

    onMounted(() => {
      if (props.isVisible) {
        startTextRotation()
        document.body.style.overflow = 'hidden'
      }
    })

    onBeforeUnmount(() => {
      stopTextRotation()
      document.body.style.overflow = ''
    })

    return {
      currentFunText,
      currentTip,
      truncateFileName,
    }
  },
}
</script>

<style lang="scss" scoped>
// Debtia farver
$debtia-green: #088c76;
$debtia-blue: #002954;

.ai-scanning-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.overlay-wrapper {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 1.3rem;
}

.overlay-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1400px;
  height: 100%;
  margin: 0 auto;
  overflow-y: auto;

  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
}

.overlay-summary-bar {
  flex-shrink: 0;
  margin-bottom: 1.5rem;
}

.scanning-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.scanning-icon {
  margin-bottom: 1.5rem;

  .icon-pulse {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, $debtia-green 0%, $debtia-blue 100%);
    color: white;
    animation: pulse 2s ease-in-out infinite;
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(8, 140, 118, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 20px rgba(8, 140, 118, 0);
  }
}

.scanning-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: $debtia-blue;
  margin-bottom: 0.5rem;
  min-height: 2rem;
  text-align: center;
  animation: fadeInOut 2.5s ease-in-out infinite;
}

@keyframes fadeInOut {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.scanning-status {
  color: #6c757d;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.current-file {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background: rgba(8, 140, 118, 0.1);
  border-radius: 2rem;
  font-size: 0.875rem;
  color: $debtia-green;
  margin-bottom: 1.5rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.progress-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  width: 100%;
}

.progress-bar {
  flex: 1;
  height: 12px;
  background: #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, $debtia-green 0%, $debtia-blue 100%);
  border-radius: 6px;
  transition: width 0.3s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 100%
    );
    animation: shimmer 1.5s infinite;
  }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.progress-text {
  font-size: 1rem;
  font-weight: 700;
  color: $debtia-green;
  min-width: 50px;
  text-align: right;
}

.files-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-height: 350px;
  overflow-y: auto;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 0.75rem;
  margin-bottom: 1.5rem;
}

.file-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;

  .file-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &.file-completed {
    background: rgba(40, 199, 111, 0.1);

    .file-name {
      color: #28c76f;
    }
  }

  &.file-processing {
    background: rgba(8, 140, 118, 0.1);
    border: 1px solid rgba(8, 140, 118, 0.3);

    .file-name {
      color: $debtia-green;
      font-weight: 700;
    }
  }

  &.file-pending {
    background: rgba(0, 0, 0, 0.02);

    .file-name {
      color: #6c757d;
    }
  }

  &.file-error {
    background: rgba(234, 84, 85, 0.1);

    .file-name {
      color: #ea5455;
    }
  }
}

.file-icon {
  flex-shrink: 0;
}

.file-icon-spinner {
  flex-shrink: 0;
  color: $debtia-green;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.file-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.875rem;
}

.file-progress {
  height: 4px;
  background: rgba(8, 140, 118, 0.2);
  border-radius: 2px;
  overflow: hidden;
  margin-left: 1.5rem;

  .file-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, $debtia-green 0%, $debtia-blue 100%);
    border-radius: 2px;
    transition: width 0.2s ease;
  }
}

.waiting-tips {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background: rgba(8, 140, 118, 0.05);
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  color: #6c757d;
  text-align: center;
  max-width: 100%;

  span {
    animation: fadeInOut 5s ease-in-out infinite;
  }
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// Responsive
@media (max-width: 768px) {
  .overlay-wrapper {
    padding: 0.75rem;
  }

  .overlay-content {
    padding: 1rem;
  }

  .overlay-summary-bar {
    margin-bottom: 1rem;
  }

  .scanning-icon .icon-pulse {
    width: 60px;
    height: 60px;
  }

  .scanning-title {
    font-size: 1.25rem;
  }

  .files-list {
    max-height: 150px;
  }
}
</style>
